import { forwardRef } from 'react';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import FormLabel from '@mui/material/FormLabel';

/**
 * Text primitives, all of them Material UI's Typography or Link.
 *
 * Heading takes a `level` (1–6) that decides the HTML element, and a separate
 * `size` that decides the appearance. Keeping those apart is the point: the
 * document outline follows where a heading sits in the page, while how big it
 * looks is a design decision. Tying them together is what produces an h3 chosen
 * because "h2 was too big", which breaks navigation for screen-reader users.
 */

const DEFAULT_SIZE_FOR_LEVEL = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h3', 5: 'h3', 6: 'h3' };

export const Heading = forwardRef(function Heading({ level = 2, size, children, ...rest }, ref) {
  const safeLevel = level >= 1 && level <= 6 ? level : 2;

  return (
    <Typography
      ref={ref}
      component={`h${safeLevel}`}
      variant={size || DEFAULT_SIZE_FOR_LEVEL[safeLevel]}
      {...rest}
    >
      {children}
    </Typography>
  );
});

/** Maps this application's vocabulary onto MUI's typography variants. */
const TEXT_VARIANTS = { body: 'body1', caption: 'body2', lead: 'h3' };

/** Maps a tone onto a theme palette path. */
const TEXT_COLORS = {
  default: 'text.primary',
  muted: 'text.secondary',
  danger: 'error.main',
  success: 'success.main',
  inverse: 'primary.contrastText'
};

export const Text = forwardRef(function Text(
  { as = 'p', tone = 'default', size = 'body', weight, children, sx, ...rest },
  ref
) {
  return (
    <Typography
      ref={ref}
      component={as === 'span' ? 'span' : 'p'}
      variant={TEXT_VARIANTS[size] ?? 'body1'}
      color={TEXT_COLORS[tone] ?? TEXT_COLORS.default}
      sx={{ fontWeight: weight === 'bold' ? 700 : weight === 'medium' ? 600 : undefined, ...sx }}
      {...rest}
    >
      {children}
    </Typography>
  );
});

/** Inline text, for when a paragraph would be wrong. */
export const Span = forwardRef(function Span(props, ref) {
  return <Text ref={ref} as="span" {...props} />;
});

/** Smaller supporting text — captions, hints, timestamps. */
export function Caption({ tone = 'muted', ...rest }) {
  return <Text as="span" size="caption" tone={tone} {...rest} />;
}

/** Emphasised inline text. */
export function Strong({ children, ...rest }) {
  return (
    <Typography component="strong" variant="body1" sx={{ fontWeight: 600 }} {...rest}>
      {children}
    </Typography>
  );
}

/**
 * A standalone form label.
 *
 * The form controls in this library are MUI TextFields, which render their own
 * label — this is for the rare control that has none of its own. `required`
 * only draws the marker; the control itself carries aria-required, so a screen
 * reader announces it once rather than twice.
 */
export const Label = forwardRef(function Label({ htmlFor, required, children, ...rest }, ref) {
  return (
    <FormLabel ref={ref} htmlFor={htmlFor} required={required} {...rest}>
      {children}
    </FormLabel>
  );
});

/**
 * The "skip to content" link.
 *
 * A real anchor with a fragment href rather than a router link: it moves focus
 * within the current document instead of navigating, and routing "#main"
 * through the router would try to match it as a path. Off-screen until focused,
 * so it costs nothing visually and is the first stop for a keyboard user.
 */
export function SkipLink({ targetId, children }) {
  return (
    <MuiLink
      href={`#${targetId}`}
      sx={{
        position: 'absolute',
        left: 4,
        top: -100,
        zIndex: 'tooltip',
        px: 4,
        py: 2,
        borderRadius: 1,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        textDecoration: 'none',
        transition: 'top 150ms ease',
        '&:focus': { top: 4 }
      }}
    >
      {children}
    </MuiLink>
  );
}

/**
 * An anchor for destinations outside the application.
 *
 * In-app navigation goes through AppNavLink in @srichaitra/common, which wraps
 * the router. This one always pairs target="_blank" with rel="noreferrer
 * noopener", so the opened page cannot reach back through window.opener.
 */
export const ExternalLink = forwardRef(function ExternalLink({ href, newTab = false, children, ...rest }, ref) {
  return (
    <MuiLink
      ref={ref}
      href={href}
      {...(newTab ? { target: '_blank', rel: 'noreferrer noopener' } : null)}
      {...rest}
    >
      {children}
    </MuiLink>
  );
});
